import styled from "styled-components";
import {
  DesignTokens,
  CanvasPanel,
  DetailPaneTitle,
  ValueOrFallback,
} from "melco-ui";
import { useField } from "formik";
import { useTranslation } from "react-i18next";
import {
  ElementListHelper,
  EfsMelcoModelsDesign,
  EfsMelcoModelsDesignElement,
  SwitchField,
} from "melco-shared-logic";
import { SelectDataMapping } from "./SelectDataMapping";
import { useDesignElementTitle } from "../../hooks/designMappings/useDesignElementTitle";
import { ServiceType } from "../../helper/serviceType";
import { DataMappingType } from "../inputFormats/SelectDataMappingType";
import { MissingFontHint } from "./MissingFontHint";
import { useEffect } from "react";
import { useServiceType } from "../../hooks/designMappings/useServiceType";

const { spacings } = DesignTokens;

const RightCanvasPanel = styled(CanvasPanel)`
  width: 300px;
`;

const ContentWrapper = styled.div`
  margin: 0 ${spacings.xlarge}px;
`;

type DesignElementDetailPanelProps = {
  inputFormatId: string;
  design: EfsMelcoModelsDesign;
  formikPrefix: string;
  currentDesignElement: EfsMelcoModelsDesignElement | undefined;
  groupIndex: (element: EfsMelcoModelsDesignElement) => number;
  setCurrentDesignElementIndex: (idx: number | undefined) => void;
  serviceType: ServiceType;
};

export const DesignElementDetailPanel: React.FC<
  DesignElementDetailPanelProps
> = ({
  inputFormatId,
  design,
  formikPrefix,
  currentDesignElement,
  groupIndex,
  setCurrentDesignElementIndex,
  serviceType,
}) => {
  const { isEmbroidery } = useServiceType();
  const { t } = useTranslation();
  const { isColor } = ElementListHelper;
  const [generateProductionFilesOnEmptyDataSwitchField] = useField(
    "outputs[0].output_production_file_on_emptydata"
  );
  const [
    deleteTextColorEmptySwitchField,
    deleteTextColorEmptySwitchMeta,
    deleteTextColorEmptySwitchHelpers,
  ] = useField([formikPrefix, "is_delete_empty"].join("."));

  const elementTitle = useDesignElementTitle(serviceType);

  const [{ value: dataMappingType }] = useField<DataMappingType | undefined>(
    [formikPrefix, "type"].join(".")
  );

  const { title, subtitle } = elementTitle(currentDesignElement, groupIndex);

  useEffect(() => {
    if (!generateProductionFilesOnEmptyDataSwitchField.value) {
      deleteTextColorEmptySwitchHelpers.setValue(false); // Turn off the switch
      deleteTextColorEmptySwitchHelpers.setTouched(false); // Optionally mark it as not touched
    }
  }, [
    generateProductionFilesOnEmptyDataSwitchField,
    deleteTextColorEmptySwitchMeta,
    deleteTextColorEmptySwitchHelpers,
  ]);

  return (
    <RightCanvasPanel elevation="left" data-cy-id="design-element-detail-panel">
      <ContentWrapper>
        <DetailPaneTitle
          title={
            <ValueOrFallback
              value={title}
              fallback={t("global.fallback.empty")}
            />
          }
          subtitle={subtitle}
          onClose={() => setCurrentDesignElementIndex(undefined)}
        />
        <SelectDataMapping
          name={[formikPrefix, "mapping_id"].join(".")}
          inputFormatId={inputFormatId}
          dataMappingType={dataMappingType}
        />
        <MissingFontHint
          design={design}
          currentDesignElement={currentDesignElement}
          formikPrefix={formikPrefix}
        />

        {currentDesignElement &&
          generateProductionFilesOnEmptyDataSwitchField.value &&
          isEmbroidery && (
            <SwitchField
              name={deleteTextColorEmptySwitchField.name}
              label={
                isColor(currentDesignElement)
                  ? t(
                      "designMappings.actions.designConfiguration.form.label.delete_color_value_empty"
                    )
                  : t(
                      "designMappings.actions.designConfiguration.form.label.delete_text_when_empty"
                    )
              }
              disabled={!generateProductionFilesOnEmptyDataSwitchField.value}
            />
          )}
      </ContentWrapper>
    </RightCanvasPanel>
  );
};
